import { useState, useEffect, useRef, useCallback } from 'react'
import { AxiosResponse } from 'axios'
import { toast } from 'sonner'
import { Loader2, ChevronDown, Plus } from 'lucide-react'
import {
  fetchCaseAttachments,
  downloadCaseAttachment,
  uploadCaseAttachment,
} from '@/services/apis/api'
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/toolTip'
import Card from '@/designs/new/components/atom/card/card'
import {
  CaseFileDownloadIcon,
  CaseFileIcon,
  CaseFilePreviewEyeOfSauronIcon,
  CasesDetailsFilesUploadIcon,
  FolderIcon,
} from '@/assets/svgIcons'

interface CaseAttachment {
  id: string
  caseId: string
  contentDocumentId: string
  versionId: string
  name: string
  fileName: string
  fileType: string
  fileExtension: string
  size: number
  description: string | null
  createdDate: string
  lastModifiedDate: string
  contentModifiedDate: string
}

interface CaseFilesProps {
  caseId?: string
}

type FetchCaseAttachmentsResponse = AxiosResponse<{
  data: {
    attachments: CaseAttachment[]
  }
}>

const CaseFiles = ({ caseId }: CaseFilesProps) => {
  const [attachments, setAttachments] = useState<CaseAttachment[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [showUploadArea, setShowUploadArea] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const limit = 40
  const listRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const inFlightRef = useRef(false)

  const loadAttachments = useCallback(
    async (currentOffset: number, isInitial = false) => {
      if (!caseId || inFlightRef.current) return
      inFlightRef.current = true

      if (isInitial) {
        setLoading(true)
      } else {
        setLoadingMore(true)
      }

      try {
        const response = (await fetchCaseAttachments({
          caseId,
          limit,
          offset: currentOffset,
        })) as FetchCaseAttachmentsResponse

        if (response.status === 200) {
          const fetched = response.data?.data?.attachments || []
          if (isInitial) {
            setAttachments(fetched)
          } else {
            setAttachments(prev => [...prev, ...fetched])
          }

          if (fetched.length < limit) {
            setHasMore(false)
          } else {
            setHasMore(true)
          }
        } else {
          toast.error('Failed to load attachments')
        }
      } catch (error) {
        console.error('Error loading attachments:', error)
        toast.error('Error loading attachments')
      } finally {
        setLoading(false)
        setLoadingMore(false)
        inFlightRef.current = false
      }
    },
    [caseId],
  )

  useEffect(() => {
    setAttachments([])
    setHasMore(true)
    inFlightRef.current = false
    loadAttachments(0, true)
  }, [caseId, loadAttachments])

  const handleScroll = useCallback(() => {
    if (
      !listRef.current ||
      loading ||
      loadingMore ||
      !hasMore ||
      isCollapsed ||
      inFlightRef.current
    )
      return
    const { scrollTop, scrollHeight, clientHeight } = listRef.current
    if (scrollHeight - scrollTop - clientHeight < 50) {
      loadAttachments(attachments.length, false)
    }
  }, [
    loading,
    loadingMore,
    hasMore,
    attachments.length,
    loadAttachments,
    isCollapsed,
  ])

  const downloadFile = async (attachment: CaseAttachment) => {
    const toastId = toast.loading('Downloading...')
    try {
      const response = (await downloadCaseAttachment(
        { versionId: attachment.versionId },
        { responseType: 'blob' },
      )) as AxiosResponse<Blob>

      if (response.status === 200) {
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'application/octet-stream',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', attachment.fileName || 'download')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        toast.success('Downloaded successfully', { id: toastId })
      } else {
        toast.error('Failed to download file', { id: toastId })
      }
    } catch (error) {
      console.error('Error downloading:', error)
      toast.error('Failed to download file', { id: toastId })
    }
  }

  const handleFileUpload = async (file: File) => {
    if (!caseId) {
      toast.error('No Case ID available to upload attachment.')
      return
    }
    setUploading(true)
    const toastId = toast.loading(`Uploading ${file.name}...`)
    try {
      const formData = new FormData()
      formData.append('caseId', caseId)
      formData.append('file', file)

      const response = (await uploadCaseAttachment(formData)) as AxiosResponse
      if (response.status === 200 || response.status === 201) {
        toast.success('File uploaded successfully', { id: toastId })
        setHasMore(true)
        loadAttachments(0, true)
      } else {
        toast.error('Failed to upload file', { id: toastId })
      }
    } catch (error) {
      console.error('Upload error:', error)
      toast.error('Upload failed', { id: toastId })
    } finally {
      setUploading(false)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0])
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDateTime = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
  }

  return (
    <Card className='w-[350px] shrink-0 h-full p-4 flex flex-col overflow-hidden bg-primary border border-border shadow-md rounded-[0.75rem]'>
      <div className='flex items-center justify-between mb-4 border-b border-secondary-border pb-3 shrink-0'>
        <div className='flex items-center gap-2'>
          <FolderIcon />
          <span className='text-lg font-bold text-primary-foreground'>
            Files
          </span>
        </div>
        <div className='flex items-center gap-2'>
          <button
            onClick={() => setShowUploadArea(!showUploadArea)}
            className='p-1 rounded hover:bg-secondary/40 text-secondary-foreground transition-colors'
            title='Toggle Upload Area'
          >
            <Plus
              className={`h-5 w-5 transition-transform duration-200 ${showUploadArea ? 'rotate-45' : ''}`}
            />
          </button>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className='p-1 rounded hover:bg-secondary/40 text-secondary-foreground transition-colors'
          >
            <ChevronDown
              className={`h-5 w-5 transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <div className='flex-1 flex flex-col min-h-0 overflow-hidden'>
          {showUploadArea && (
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className='border-2 border-dashed border-border bg-tertiary hover:bg-secondary rounded-lg p-5 flex flex-col items-center justify-center text-center cursor-pointer mb-4 transition-colors shrink-0 relative'
            >
              {uploading ? (
                <div className='flex flex-col items-center justify-center space-y-2 py-2'>
                  <Loader2 className='h-8 w-8 animate-spin text-[#2563EB]' />
                  <span className='text-xs text-[#2563EB] font-medium'>
                    Uploading file...
                  </span>
                </div>
              ) : (
                <>
                  <CasesDetailsFilesUploadIcon />
                  <p className='text-md font-normal text-tertiary-foreground mt-2'>
                    Drag & drop file here
                  </p>
                  <p className='text-md text-link underline font-medium mt-1'>
                    Click to browse
                  </p>
                </>
              )}
              <input
                ref={fileInputRef}
                type='file'
                className='hidden'
                disabled={uploading}
                onChange={e =>
                  e.target.files?.[0] && handleFileUpload(e.target.files[0])
                }
              />
            </div>
          )}

          <div
            ref={listRef}
            onScroll={handleScroll}
            className='flex-1 overflow-y-auto spec-scroll pr-1 space-y-3'
          >
            {loading && attachments.length === 0 ? (
              <div className='flex flex-col items-center justify-center h-32 space-y-2'>
                <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
                <span className='text-xs text-muted-foreground'>
                  Loading files...
                </span>
              </div>
            ) : attachments.length === 0 ? (
              <div className='text-center text-sm text-muted-foreground py-8'>
                No files available.
              </div>
            ) : (
              attachments.map(file => (
                <div
                  key={file.id}
                  className='flex items-center gap-3 p-3 rounded-lg hover:bg-tertiary transition-colors'
                >
                  <CaseFileIcon />
                  <div className='flex-1 min-w-0'>
                    <p
                      className='text-sm font-semibold text-primary-foreground truncate'
                      title={file.fileName}
                    >
                      {file.fileName}
                    </p>
                    <p className='text-xs text-secondary-foreground mt-0.5'>
                      {formatDateTime(file.createdDate)}
                    </p>
                    <p className='text-[10px] text-muted-foreground mt-0.5'>
                      {formatFileSize(file.size)}
                    </p>
                  </div>

                  <div className='flex items-center gap-2 shrink-0 ml-1'>
                    <PreviewTooltip attachment={file}>
                      <button className='p-1.5 rounded hover:bg-primary text-secondary-foreground hover:text-primary-foreground transition-colors'>
                        <CaseFilePreviewEyeOfSauronIcon />
                      </button>
                    </PreviewTooltip>
                    <button
                      onClick={() => downloadFile(file)}
                      className='p-1.5 rounded hover:bg-primary text-secondary-foreground hover:text-primary-foreground transition-colors'
                    >
                      <CaseFileDownloadIcon />
                    </button>
                  </div>
                </div>
              ))
            )}

            {loadingMore && (
              <div className='flex justify-center py-2'>
                <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  )
}

export default CaseFiles

const PreviewTooltip = ({
  attachment,
  children,
}: {
  attachment: CaseAttachment
  children: React.ReactNode
}) => {
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const extension = attachment.fileName?.split('.').pop()?.toLowerCase() || ''
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(
    extension,
  )
  const isSafeToPreview =
    ['pdf', 'txt', 'csv', 'json', 'xml', 'md'].includes(extension) || isImage

  const handleOpenChange = async (open: boolean) => {
    setIsOpen(open)
    if (open && !url && !loading) {
      if (!isSafeToPreview) return
      setLoading(true)
      try {
        const response = (await downloadCaseAttachment(
          { versionId: attachment.versionId },
          { responseType: 'blob' },
        )) as AxiosResponse<Blob>

        if (response.status === 200) {
          const mimeTypes: { [key: string]: string } = {
            pdf: 'application/pdf',
            jpg: 'image/jpeg',
            jpeg: 'image/jpeg',
            png: 'image/png',
            txt: 'text/plain',
            csv: 'text/csv',
          }

          const mimeType = mimeTypes[extension] || 'application/octet-stream'
          const blob = new Blob([response.data], { type: mimeType })
          setUrl(window.URL.createObjectURL(blob))
        }
      } catch (error) {
        console.error('Error fetching preview:', error)
      } finally {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    return () => {
      if (url) window.URL.revokeObjectURL(url)
    }
  }, [url])

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip open={isOpen} onOpenChange={handleOpenChange}>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent
          side='left'
          align='center'
          className='w-[600px] h-[500px] p-2 mb-10 bg-primary flex flex-col shadow-xl border z-[9999]'
        >
          <div className='text-xs font-bold mb-2 truncate border-b pb-1'>
            {attachment.fileName}
          </div>
          <div className='flex-1 w-full h-full bg-primary flex items-center justify-center overflow-hidden'>
            {!isSafeToPreview ? (
              <div className='text-sm text-primary-foreground text-center px-4'>
                Preview not supported for <b>.{extension}</b> files.
                <br />
                Please click the download icon instead.
              </div>
            ) : loading ? (
              <div className='text-sm text-primary-foreground'>
                Loading preview...
              </div>
            ) : url ? (
              isImage ? (
                <img
                  src={url}
                  alt='Preview'
                  className='w-full h-full object-contain'
                />
              ) : (
                <iframe
                  src={url}
                  className='w-full h-full border-0'
                  title='Preview'
                />
              )
            ) : (
              <div className='text-sm text-muted-foreground'>
                Preview failed to load.
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
